import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CalendarDaysIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import {
  fetchFormInstanceDetail,
  submitFormInstanceThunk,
  updateFormInstanceThunk,
  toggleInstanceLockThunk,
} from '../store/slices/formInstanceSlice'
import api from '../services/api'
import type {
  FormInspectionFrequency,
  FormSubmissionValue,
  FormTemplateField,
  FormLogicRule,
  FormTemplateVersionDetail,
} from '@/types'
import { FormFieldType, FormInstanceStatus, FormLogicAction, FormLogicOperator, FormSubmissionStatus, FormType } from '@/types'
import { useToast } from '../components/ToastContainer'

const frequencyLabelMap: Record<FormInspectionFrequency, string> = {
  NONE: '不指定',
  DAILY: '每日',
  WEEKLY: '每週（週一）',
  MONTHLY: '每月（1 日）',
  QUARTERLY: '每季（1 日）',
}

const statusBadge = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return 'bg-green-100 text-green-700'
    case 'IN_PROGRESS':
      return 'bg-yellow-100 text-yellow-700'
    case 'OVERDUE':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

const formatDate = (value?: string | null) => {
  if (!value) return '未設定'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

interface UploadedAttachment {
  id: string
  name: string
  url: string
  mimeType?: string
  size?: number
}

interface DateRangeValue {
  startDate?: string
  endDate?: string
}

type FieldControlValue =
  | string
  | string[]
  | boolean
  | null
  | undefined
  | UploadedAttachment[]
  | DateRangeValue

const textFieldTypes = new Set<FormFieldType>([
  FormFieldType.SHORT_TEXT,
  FormFieldType.LONG_TEXT,
  FormFieldType.RICH_TEXT,
])

const multilineFieldTypes = new Set<FormFieldType>([FormFieldType.LONG_TEXT, FormFieldType.RICH_TEXT])

const numberFieldTypes = new Set<FormFieldType>([
  FormFieldType.INTEGER,
  FormFieldType.DECIMAL,
  FormFieldType.PERCENTAGE,
  FormFieldType.CURRENCY,
  FormFieldType.RANGE,
  FormFieldType.SCALE,
  FormFieldType.RATING,
])

const booleanFieldTypes = new Set<FormFieldType>([
  FormFieldType.BOOLEAN,
  FormFieldType.CHECKBOX,
])

const selectFieldTypes = new Set<FormFieldType>([
  FormFieldType.SINGLE_SELECT,
  FormFieldType.DROPDOWN,
])

const multiSelectFieldTypes = new Set<FormFieldType>([FormFieldType.MULTI_SELECT])

const dateFieldTypes = new Set<FormFieldType>([FormFieldType.DATE, FormFieldType.DATETIME, FormFieldType.TIME])

const dateRangeFieldTypes = new Set<FormFieldType>([FormFieldType.DATE_RANGE])

const fileUploadFieldTypes = new Set<FormFieldType>([
  FormFieldType.ATTACHMENT,
  FormFieldType.IMAGE,
  FormFieldType.VIDEO,
])

const unsupportedFieldTypes = new Set<FormFieldType>([
  FormFieldType.SIGNATURE,
  FormFieldType.QR_CODE,
  FormFieldType.BARCODE,
])

const paragraphFields = new Set<FormFieldType>([FormFieldType.PARAGRAPH])

const normalizeAttachmentList = (raw: unknown): UploadedAttachment[] => {
  if (!Array.isArray(raw)) {
    return []
  }

  return raw
    .map((item, index) => {
      if (typeof item === 'string') {
        const filename = item.split('/').pop() ?? `附件 ${index + 1}`
        return {
          id: `default-${index}`,
          name: filename,
          url: item,
        }
      }

      if (typeof item === 'object' && item !== null) {
        const record = item as Record<string, unknown>
        const url =
          (typeof record.url === 'string' && record.url) ||
          (typeof record.link === 'string' && record.link) ||
          (typeof record.filepath === 'string' && record.filepath) ||
          ''

        if (!url) {
          return null
        }

        const nameCandidate =
          (typeof record.name === 'string' && record.name) ||
          (typeof record.originalName === 'string' && record.originalName) ||
          url.split('/').pop() ||
          `附件 ${index + 1}`

        return {
          id: (typeof record.id === 'string' && record.id) || `attachment-${index}`,
          name: nameCandidate,
          url,
          mimeType: typeof record.mimeType === 'string' ? record.mimeType : undefined,
          size: typeof record.size === 'number' ? record.size : undefined,
        }
      }

      return null
    })
    .filter((item): item is UploadedAttachment => Boolean(item))
}

const normalizeDateRangeValue = (raw: unknown): DateRangeValue => {
  if (!raw || typeof raw !== 'object') {
    return { startDate: '', endDate: '' }
  }

  const record = raw as Record<string, unknown>
  const startValue =
    (typeof record.startDate === 'string' && record.startDate) ||
    (typeof record.start === 'string' && record.start) ||
    undefined
  const endValue =
    (typeof record.endDate === 'string' && record.endDate) ||
    (typeof record.end === 'string' && record.end) ||
    undefined

  return {
    startDate: startValue ? formatDateForInput(startValue) : '',
    endDate: endValue ? formatDateForInput(endValue) : '',
  }
}

const formatDateForInput = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString().slice(0, 10)
}

const formatDateTimeForInput = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const iso = date.toISOString()
  return iso.slice(0, 16)
}

const formatTimeForInput = (value: string) => {
  const date = new Date(`1970-01-01T${value}`)
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().slice(11, 16)
  }
  if (/^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5)
  }
  return ''
}

const coerceDefaultValue = (field: FormTemplateField): FieldControlValue => {
  const raw = field.defaultValue

  if (raw === undefined || raw === null) {
    if (multiSelectFieldTypes.has(field.type)) return []
    if (fileUploadFieldTypes.has(field.type)) return []
    if (dateRangeFieldTypes.has(field.type)) return { startDate: '', endDate: '' }
    if (textFieldTypes.has(field.type) || numberFieldTypes.has(field.type) || dateFieldTypes.has(field.type)) {
      return ''
    }
    return null
  }

  if (multiSelectFieldTypes.has(field.type)) {
    if (Array.isArray(raw)) {
      return raw.map(String)
    }
    if (typeof raw === 'string' && raw.length > 0) {
      return raw.split(',').map(item => item.trim()).filter(Boolean)
    }
    return []
  }

  if (booleanFieldTypes.has(field.type)) {
    if (typeof raw === 'boolean') return raw
    if (typeof raw === 'string') return raw === 'true'
    return null
  }

  if (numberFieldTypes.has(field.type)) {
    if (typeof raw === 'number') return raw.toString()
    if (typeof raw === 'string') return raw
    return ''
  }

  if (dateFieldTypes.has(field.type)) {
    if (typeof raw === 'string') {
      if (field.type === FormFieldType.DATE) return formatDateForInput(raw)
      if (field.type === FormFieldType.DATETIME) return formatDateTimeForInput(raw)
      if (field.type === FormFieldType.TIME) return formatTimeForInput(raw)
    }
    return ''
  }

  if (dateRangeFieldTypes.has(field.type)) {
    return normalizeDateRangeValue(raw)
  }

  if (fileUploadFieldTypes.has(field.type)) {
    return normalizeAttachmentList(raw)
  }

  if (textFieldTypes.has(field.type) || field.type === FormFieldType.PARAGRAPH) {
    if (typeof raw === 'string') return raw
    return String(raw)
  }

  return raw as FieldControlValue
}

const isEmptyValue = (value: FieldControlValue) => {
  if (value === null || value === undefined) {
    return true
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  if (typeof value === 'string') {
    return value.trim().length === 0
  }
  if (typeof value === 'boolean') {
    return false
  }
  if (typeof value === 'object') {
    const range = value as DateRangeValue
    const startEmpty = !range.startDate || range.startDate.trim().length === 0
    const endEmpty = !range.endDate || range.endDate.trim().length === 0
    return startEmpty && endEmpty
  }
  return false
}

interface ComputedLogicState {
  visible: Record<string, boolean>
  required: Record<string, boolean>
  disabled: Record<string, boolean>
  clear: Set<string>
}

const parseBoolean = (input: unknown): boolean => {
  if (typeof input === 'boolean') return input
  if (typeof input === 'string') {
    const normalized = input.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === '1' || normalized === 'yes') return true
  }
  if (typeof input === 'number') {
    return input !== 0
  }
  return false
}

const toNumber = (input: unknown): number | null => {
  if (typeof input === 'number') {
    return Number.isNaN(input) ? null : input
  }
  if (typeof input === 'string' && input.trim().length > 0) {
    const parsed = Number(input)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

const toTimestamp = (input: unknown): number | null => {
  if (typeof input !== 'string' || input.trim().length === 0) {
    return null
  }
  const parsed = Date.parse(input)
  return Number.isNaN(parsed) ? null : parsed
}

const evaluateLogicCondition = (
  field: FormTemplateField | undefined,
  value: FieldControlValue,
  operator: FormLogicOperator,
  comparisonValue: unknown,
): boolean => {
  if (operator === FormLogicOperator.IS_EMPTY) {
    return isEmptyValue(value)
  }
  if (operator === FormLogicOperator.IS_NOT_EMPTY) {
    return !isEmptyValue(value)
  }

  const comparisonList = Array.isArray(comparisonValue)
    ? comparisonValue
    : [comparisonValue]

  if (field && multiSelectFieldTypes.has(field.type)) {
    const current = Array.isArray(value) ? value.map(String) : []
    const comparisonStrings = comparisonList.map(item => String(item ?? ''))
    switch (operator) {
      case FormLogicOperator.EQUALS:
      case FormLogicOperator.CONTAINS:
      case FormLogicOperator.IN:
        return comparisonStrings.some(item => current.includes(item))
      case FormLogicOperator.NOT_EQUALS:
      case FormLogicOperator.NOT_CONTAINS:
      case FormLogicOperator.NOT_IN:
        return comparisonStrings.every(item => !current.includes(item))
      default:
        return false
    }
  }

  if (field && booleanFieldTypes.has(field.type)) {
    const comparisonBool = parseBoolean(comparisonList[0])
    const currentBool = parseBoolean(value)

    switch (operator) {
      case FormLogicOperator.EQUALS:
      case FormLogicOperator.IN:
        return currentBool === comparisonBool
      case FormLogicOperator.NOT_EQUALS:
      case FormLogicOperator.NOT_IN:
        return currentBool !== comparisonBool
      default:
        return false
    }
  }

  if (field && numberFieldTypes.has(field.type)) {
    const numericValue = toNumber(value)
    const comparisonNumeric = toNumber(comparisonList[0])

    if (numericValue === null || comparisonNumeric === null) {
      return false
    }

    switch (operator) {
      case FormLogicOperator.EQUALS:
        return numericValue === comparisonNumeric
      case FormLogicOperator.NOT_EQUALS:
        return numericValue !== comparisonNumeric
      case FormLogicOperator.GREATER_THAN:
        return numericValue > comparisonNumeric
      case FormLogicOperator.GREATER_OR_EQUAL:
        return numericValue >= comparisonNumeric
      case FormLogicOperator.LESS_THAN:
        return numericValue < comparisonNumeric
      case FormLogicOperator.LESS_OR_EQUAL:
        return numericValue <= comparisonNumeric
      case FormLogicOperator.IN:
        return comparisonList.some(item => toNumber(item) === numericValue)
      case FormLogicOperator.NOT_IN:
        return comparisonList.every(item => toNumber(item) !== numericValue)
      default:
        return false
    }
  }

  if (field && (dateFieldTypes.has(field.type) || dateRangeFieldTypes.has(field.type))) {
    const currentTimestamp =
      typeof value === 'string'
        ? toTimestamp(value)
        : typeof value === 'object' && value !== null && 'startDate' in (value as DateRangeValue)
        ? toTimestamp((value as DateRangeValue).startDate)
        : null

    const comparisonTimestamp = toTimestamp(comparisonList[0])

    if (currentTimestamp === null || comparisonTimestamp === null) {
      return false
    }

    switch (operator) {
      case FormLogicOperator.EQUALS:
        return currentTimestamp === comparisonTimestamp
      case FormLogicOperator.NOT_EQUALS:
        return currentTimestamp !== comparisonTimestamp
      case FormLogicOperator.GREATER_THAN:
        return currentTimestamp > comparisonTimestamp
      case FormLogicOperator.GREATER_OR_EQUAL:
        return currentTimestamp >= comparisonTimestamp
      case FormLogicOperator.LESS_THAN:
        return currentTimestamp < comparisonTimestamp
      case FormLogicOperator.LESS_OR_EQUAL:
        return currentTimestamp <= comparisonTimestamp
      default:
        return false
    }
  }

  const valueString =
    value === null || value === undefined
      ? ''
      : Array.isArray(value)
      ? value.map(item => String(item ?? '')).join(',')
      : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value)

  const comparisonStrings = comparisonList.map(item =>
    item === null || item === undefined ? '' : String(item),
  )

  switch (operator) {
    case FormLogicOperator.EQUALS:
      return comparisonStrings.some(item => valueString === item)
    case FormLogicOperator.NOT_EQUALS:
      return comparisonStrings.every(item => valueString !== item)
    case FormLogicOperator.CONTAINS:
      return comparisonStrings.some(item => valueString.includes(item))
    case FormLogicOperator.NOT_CONTAINS:
      return comparisonStrings.every(item => !valueString.includes(item))
    case FormLogicOperator.IN:
      return comparisonStrings.includes(valueString)
    case FormLogicOperator.NOT_IN:
      return !comparisonStrings.includes(valueString)
    case FormLogicOperator.GREATER_THAN:
      return valueString > comparisonStrings[0]
    case FormLogicOperator.GREATER_OR_EQUAL:
      return valueString >= comparisonStrings[0]
    case FormLogicOperator.LESS_THAN:
      return valueString < comparisonStrings[0]
    case FormLogicOperator.LESS_OR_EQUAL:
      return valueString <= comparisonStrings[0]
    default:
      return false
  }
}

interface FieldInputRowProps {
  field: FormTemplateField
  value: FieldControlValue
  error?: string
  disabled?: boolean
  isRequired?: boolean
  onChange: (value: FieldControlValue) => void
}

const uploadConfigByType: Partial<Record<FormFieldType, { endpoint: string; defaultMaxSizeMB: number; accept?: string }>> = {
  [FormFieldType.ATTACHMENT]: { endpoint: '/upload/file', defaultMaxSizeMB: 50 },
  [FormFieldType.IMAGE]: { endpoint: '/upload/image', defaultMaxSizeMB: 10, accept: 'image/*' },
  [FormFieldType.VIDEO]: { endpoint: '/upload/video', defaultMaxSizeMB: 50, accept: 'video/*' },
}

const getUploadConfig = (type: FormFieldType) =>
  uploadConfigByType[type] ?? uploadConfigByType[FormFieldType.ATTACHMENT]!

const FieldInputRow = ({
  field,
  value,
  error,
  disabled = false,
  isRequired,
  onChange,
}: FieldInputRowProps) => {
  const metadata = (field.metadata ?? {}) as Record<string, unknown>
  const unit = metadata.unit
  const showUnit = typeof unit === 'string' ? unit.trim().length > 0 : unit !== undefined && unit !== null
  const options = Array.isArray(field.options)
    ? [...field.options].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    : []
  const required = isRequired ?? Boolean(field.isRequired)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const updateValue = (next: FieldControlValue) => {
    if (disabled) return
    onChange(next)
  }

  const rawMaxFiles =
    typeof metadata.maxFiles === 'number'
      ? metadata.maxFiles
      : typeof metadata.maxFiles === 'string'
      ? Number(metadata.maxFiles)
      : undefined
  const maxFiles =
    rawMaxFiles !== undefined && !Number.isNaN(rawMaxFiles)
      ? Math.max(0, Math.floor(rawMaxFiles))
      : undefined

  const allowMultipleUploads = Boolean(field.isRepeatable || (maxFiles ?? 0) > 1)

  const handleRemoveAttachment = (attachmentId: string) => {
    if (disabled || !fileUploadFieldTypes.has(field.type)) return
    const existingAttachments: UploadedAttachment[] = Array.isArray(value)
      ? (value as unknown[])
          .map(item =>
            item && typeof item === 'object' && 'id' in (item as Record<string, unknown>) && 'url' in (item as Record<string, unknown>)
              ? (item as UploadedAttachment)
              : null,
          )
          .filter((item): item is UploadedAttachment => item !== null)
      : []
    if (existingAttachments.length === 0) return
    const filtered = existingAttachments.filter(item => item.id !== attachmentId)
    updateValue(filtered)
  }

  const handleFileUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || disabled || !fileUploadFieldTypes.has(field.type)) return

    const config = getUploadConfig(field.type)
    const existingAttachments: UploadedAttachment[] = Array.isArray(value)
      ? (value as unknown[])
          .map(item =>
            item && typeof item === 'object' && 'id' in (item as Record<string, unknown>) && 'url' in (item as Record<string, unknown>)
              ? (item as UploadedAttachment)
              : null,
          )
          .filter((item): item is UploadedAttachment => item !== null)
      : []
    const availableSlots = maxFiles !== undefined ? Math.max(maxFiles - existingAttachments.length, 0) : undefined

    if (availableSlots !== undefined && availableSlots <= 0) {
      setUploadError(`已達上傳數量上限（最多 ${maxFiles} 件）`)
      return
    }

    const queue = Array.from(fileList).slice(
      0,
      availableSlots !== undefined ? availableSlots : fileList.length,
    )

    if (queue.length === 0) {
      return
    }

    const configuredMaxSize =
      typeof metadata.maxSizeMB === 'number'
        ? metadata.maxSizeMB
        : typeof metadata.maxSizeMB === 'string'
        ? Number(metadata.maxSizeMB)
        : undefined
    const maxSizeMB =
      configuredMaxSize !== undefined && !Number.isNaN(configuredMaxSize)
        ? Math.max(1, configuredMaxSize)
        : config.defaultMaxSizeMB

    setUploadError(null)
    setUploading(true)

    try {
      const uploaded: UploadedAttachment[] = []

      for (const file of queue) {
        if (file.size > maxSizeMB * 1024 * 1024) {
          setUploadError(`「${file.name}」超過 ${maxSizeMB} MB 限制`)
          continue
        }

        try {
          const formData = new FormData()
          formData.append('file', file)
          const response = await api.post(config.endpoint, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })

          const data = response.data ?? {}
          const link: string | undefined = data.link ?? data.url
          const id: unknown = data.id

          if (!link || typeof id !== 'string') {
            throw new Error('伺服器未回傳附件資訊')
          }

          uploaded.push({
            id,
            name: (typeof data.name === 'string' && data.name) || file.name,
            url: link,
            mimeType: file.type,
            size: file.size,
          })
        } catch (err) {
          const message = err instanceof Error ? err.message : '上傳失敗'
          setUploadError(message)
        }
      }

      if (uploaded.length > 0) {
        const merged: UploadedAttachment[] = [...existingAttachments, ...uploaded]
        const trimmed = maxFiles !== undefined ? merged.slice(0, maxFiles) : merged
        updateValue(trimmed)
      }
    } finally {
      setUploading(false)
    }
  }

  const renderInput = () => {
    if (paragraphFields.has(field.type)) {
      return (
        <div className="rounded border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          {field.description ?? '此欄位為段落說明文字，不需填寫。'}
        </div>
      )
    }

    if (unsupportedFieldTypes.has(field.type)) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
          此欄位類型尚未支援線上填寫，請改以附件或其他方式回報。
        </div>
      )
    }

    if (dateRangeFieldTypes.has(field.type)) {
      const rangeValue =
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as DateRangeValue)
          : { startDate: '', endDate: '' }

      return (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={rangeValue.startDate ?? ''}
            onChange={(event) =>
              updateValue({
                ...rangeValue,
                startDate: event.target.value,
              })
            }
            disabled={disabled}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
          />
          <span className="text-xs text-gray-500">至</span>
          <input
            type="date"
            value={rangeValue.endDate ?? ''}
            onChange={(event) =>
              updateValue({
                ...rangeValue,
                endDate: event.target.value,
              })
            }
            disabled={disabled}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
          />
        </div>
      )
    }

    if (fileUploadFieldTypes.has(field.type)) {
      const attachments: UploadedAttachment[] = Array.isArray(value)
        ? (value as unknown[])
            .map(item =>
              item && typeof item === 'object' && 'id' in (item as Record<string, unknown>) && 'url' in (item as Record<string, unknown>)
                ? (item as UploadedAttachment)
                : null,
            )
            .filter((item): item is UploadedAttachment => item !== null)
        : []
      const config = getUploadConfig(field.type)
      const customAccept = typeof metadata.accept === 'string' ? metadata.accept : undefined
      const accept = customAccept ?? config.accept
      const availableSlots = maxFiles !== undefined ? Math.max(maxFiles - attachments.length, 0) : undefined
      const canAddMore = availableSlots === undefined || availableSlots > 0
      const configuredMaxSize =
        typeof metadata.maxSizeMB === 'number'
          ? metadata.maxSizeMB
          : typeof metadata.maxSizeMB === 'string'
          ? Number(metadata.maxSizeMB)
          : undefined
      const maxSizeHint =
        configuredMaxSize !== undefined && !Number.isNaN(configuredMaxSize)
          ? configuredMaxSize
          : config.defaultMaxSizeMB

      return (
        <div className="mt-2 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${
                disabled || uploading || !canAddMore
                  ? 'cursor-not-allowed border-gray-200 text-gray-400'
                  : 'cursor-pointer border-primary-300 text-primary-600 hover:bg-primary-50'
              }`}
            >
              <span>{uploading ? '上傳中…' : '選擇檔案'}</span>
              <input
                type="file"
                className="hidden"
                accept={accept}
                multiple={allowMultipleUploads && (availableSlots === undefined || availableSlots > 1)}
                disabled={disabled || uploading || !canAddMore}
                onChange={(event) => {
                  handleFileUpload(event.target.files)
                  event.target.value = ''
                }}
              />
            </label>
            {maxFiles !== undefined && (
              <span className="text-xs text-gray-500">
                {attachments.length}/{maxFiles} 件
              </span>
            )}
            <span className="text-xs text-gray-400">
              檔案上限 {maxSizeHint} MB
            </span>
          </div>
          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
          {attachments.length > 0 && (
            <ul className="space-y-2">
              {attachments.map(attachment => (
                <li
                  key={attachment.id}
                  className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                >
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="max-w-[60%] truncate text-primary-600 hover:underline"
                  >
                    {attachment.name}
                  </a>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {typeof attachment.size === 'number' && (
                      <span>{(attachment.size / (1024 * 1024)).toFixed(1)} MB</span>
                    )}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(attachment.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        移除
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!canAddMore && (
            <p className="text-xs text-gray-500">已達上限，移除既有檔案後可再新增。</p>
          )}
        </div>
      )
    }

    if (numberFieldTypes.has(field.type)) {
      const inputProps: Record<string, unknown> = {}
      if (typeof metadata.min === 'number') inputProps.min = metadata.min
      if (typeof metadata.max === 'number') inputProps.max = metadata.max
      if (typeof metadata.step === 'number') inputProps.step = metadata.step
      const numericValue =
        typeof value === 'string'
          ? value
          : value === null || value === undefined
          ? ''
          : String(value)
      return (
        <input
          type="number"
          value={numericValue}
          onChange={(event) => updateValue(event.target.value)}
          disabled={disabled}
          className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
          {...inputProps}
        />
      )
    }

    if (selectFieldTypes.has(field.type)) {
      if (options.length === 0) {
        return <p className="text-sm text-gray-500">此欄位尚未設定選項。</p>
      }
      return (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateValue(event.target.value || '')}
          disabled={disabled}
          className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
        >
          <option value="">請選擇</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )
    }

    if (multiSelectFieldTypes.has(field.type)) {
      if (options.length === 0) {
        return <p className="text-sm text-gray-500">此欄位尚未設定選項。</p>
      }

      const current: string[] = Array.isArray(value)
        ? value
            .map(item => (typeof item === 'string' ? item : null))
            .filter((item): item is string => item !== null)
        : []

      return (
        <div className="mt-2 space-y-2">
          {options.map(option => {
            const checked = current.includes(option.value)
            return (
              <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => {
                    if (checked) {
                      updateValue(current.filter(item => item !== option.value))
                    } else {
                      updateValue([...current, option.value])
                    }
                  }}
                />
                <span>{option.label}</span>
              </label>
            )
          })}
        </div>
      )
    }

    if (booleanFieldTypes.has(field.type)) {
      const checked = typeof value === 'boolean' ? value : false
      return (
        <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            checked={checked}
            disabled={disabled}
            onChange={(event) => updateValue(event.target.checked)}
          />
          <span>{field.description ?? '是 / 否'}</span>
        </label>
      )
    }

    if (dateFieldTypes.has(field.type)) {
      const inputType =
        field.type === FormFieldType.DATE
          ? 'date'
          : field.type === FormFieldType.TIME
          ? 'time'
          : 'datetime-local'
      return (
        <input
          type={inputType}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateValue(event.target.value)}
          disabled={disabled}
          className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
        />
      )
    }

    if (textFieldTypes.has(field.type)) {
      if (multilineFieldTypes.has(field.type)) {
        return (
          <textarea
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => updateValue(event.target.value)}
            disabled={disabled}
            className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
            rows={4}
          />
        )
      }

      return (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateValue(event.target.value)}
          disabled={disabled}
          className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
          placeholder={field.placeholder ?? undefined}
        />
      )
    }

    return (
      <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
        尚未支援的欄位類型：{field.type}
      </div>
    )
  }

  return (
    <li className="rounded border border-gray-200 bg-gray-50 px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800">{field.label}</span>
            {required && <span className="rounded bg-red-100 px-2 py-0.5 text-[11px] text-red-600">必填</span>}
            {field.isRepeatable && (
              <span className="rounded bg-indigo-100 px-2 py-0.5 text-[11px] text-indigo-600">可重複</span>
            )}
          </div>
          {field.description && <p className="mt-1 text-xs text-gray-500">{field.description}</p>}
          {showUnit && (
            <p className="mt-1 text-xs text-gray-500">
              單位：{String(unit)}
            </p>
          )}
        </div>
        <span className="whitespace-nowrap text-xs text-gray-400">類型：{field.type}</span>
      </div>
      {renderInput()}
      {field.helpText && <p className="mt-2 text-xs text-gray-500">{field.helpText}</p>}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {field.isRepeatable && (
        <p className="mt-2 text-xs text-yellow-600">
          注意：目前版本尚未支援直接新增多筆重複項目，請在下方描述欄補充。
        </p>
      )}
    </li>
  )
}

const ProjectFormInstanceDetail = () => {
  const { projectKey, instanceId } = useParams<{ projectKey: string; instanceId: string }>()
  const dispatch = useAppDispatch()
  const { activeInstance, loading, error, lockUpdating } = useAppSelector(state => state.formInstances)
  const { showError, showSuccess } = useToast()
  const [formValues, setFormValues] = useState<Record<string, FieldControlValue>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (instanceId) {
      dispatch(fetchFormInstanceDetail(instanceId))
    }
  }, [dispatch, instanceId])

  const templateVersion = activeInstance?.templateVersion as FormTemplateVersionDetail | undefined

  const fields = useMemo(() => templateVersion?.fields ?? [], [templateVersion])
  const sections = templateVersion?.sections ?? []

  const logicRules: FormLogicRule[] = useMemo(() => {
    if (Array.isArray(templateVersion?.logicRules)) {
      return templateVersion.logicRules
    }
    return []
  }, [templateVersion])

  const defaultValues = useMemo(() => {
    const initial: Record<string, FieldControlValue> = {}
    fields.forEach(field => {
      initial[field.id] = coerceDefaultValue(field)
    })
    return initial
  }, [fields])

  useEffect(() => {
    setFormValues(defaultValues)
    setFieldErrors({})
  }, [defaultValues])

  const fieldById = useMemo(() => {
    const map: Record<string, FormTemplateField> = {}
    fields.forEach(field => {
      map[field.id] = field
    })
    return map
  }, [fields])

  const logicState = useMemo<ComputedLogicState>(() => {
    if (!logicRules || logicRules.length === 0) {
      const initialVisibility: Record<string, boolean> = {}
      fields.forEach(field => {
        initialVisibility[field.id] = true
      })
      return {
        visible: initialVisibility,
        required: {},
        disabled: {},
        clear: new Set(),
      }
    }

    const visibility = new Map<string, boolean>()
    const required = new Map<string, boolean>()
    const disabled = new Map<string, boolean>()
    const clear = new Set<string>()

    fields.forEach(field => {
      visibility.set(field.id, true)
    })

    const orderedRules = [...logicRules].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

    orderedRules.forEach(rule => {
      const triggerField = fieldById[rule.triggerFieldId]
      const effectField = fieldById[rule.effectFieldId]
      if (!effectField) return

      const triggerValue = formValues[rule.triggerFieldId]
      const conditionMet = evaluateLogicCondition(triggerField, triggerValue, rule.operator, rule.comparisonValue)

      if (!conditionMet) {
        return
      }

      switch (rule.action) {
        case FormLogicAction.HIDE:
          visibility.set(effectField.id, false)
          break
        case FormLogicAction.SHOW:
          visibility.set(effectField.id, true)
          break
        case FormLogicAction.DISABLE:
          disabled.set(effectField.id, true)
          break
        case FormLogicAction.ENABLE:
          disabled.set(effectField.id, false)
          break
        case FormLogicAction.REQUIRE:
          required.set(effectField.id, true)
          break
        case FormLogicAction.OPTIONAL:
          required.set(effectField.id, false)
          break
        case FormLogicAction.CLEAR_VALUE:
          clear.add(effectField.id)
          break
        case FormLogicAction.SET_VALUE:
          // TODO: 後續支援自動帶入值邏輯
          break
        default:
          break
      }
    })

    return {
      visible: Object.fromEntries(visibility.entries()),
      required: Object.fromEntries(required.entries()),
      disabled: Object.fromEntries(disabled.entries()),
      clear,
    }
  }, [fieldById, fields, formValues, logicRules])

  useEffect(() => {
    if (logicState.clear.size === 0) {
      return
    }

    setFormValues(prev => {
      let changed = false
      const next = { ...prev }
      logicState.clear.forEach(fieldId => {
        const targetField = fieldById[fieldId]
        if (!targetField) return
        const defaultValue = coerceDefaultValue(targetField)
        if (!Object.is(next[fieldId], defaultValue)) {
          next[fieldId] = defaultValue
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [fieldById, logicState.clear])

  const groupedFields = useMemo(() => {
    if (sections.length > 0) {
      const map = sections.map(section => ({
        key: section.id,
        title: section.title || '未命名區塊',
        description: section.description ?? '',
        fields: fields.filter(field => field.sectionId === section.id),
      }))
      const unassigned = fields.filter(field => !field.sectionId)
      if (unassigned.length > 0) {
        map.push({
          key: 'unassigned',
          title: '未分類欄位',
          description: '',
          fields: unassigned,
        })
      }
      return map.filter(group => group.fields.length > 0)
    }

    const grouped = new Map<string, FormTemplateField[]>()
    fields.forEach(field => {
      const category = field.category?.trim() || '未分類'
      if (!grouped.has(category)) {
        grouped.set(category, [])
      }
      grouped.get(category)!.push(field)
    })

    return Array.from(grouped.entries()).map(([title, fieldList]) => ({
      key: title,
      title,
      description: '',
      fields: fieldList,
    }))
  }, [fields, sections])

  const assignments = activeInstance?.assignments ?? []
  const metadata = (activeInstance?.metadata ?? {}) as Record<string, unknown>
  const scheduleFieldIds = Array.isArray(metadata.scheduleFieldIds) ? metadata.scheduleFieldIds.length : 0
  const scheduleDate = typeof metadata.scheduleDate === 'string' ? metadata.scheduleDate : null
  const autoGenerated = Boolean(metadata.autoGenerated)
  const isLocked = Boolean(metadata.locked)
  const lockedAt = typeof metadata.lockedAt === 'string' ? metadata.lockedAt : null
  const scheduleRule = activeInstance?.scheduleRule
  const submissions = activeInstance?.submissions ?? []
  const activityLog = Array.isArray(metadata.activityLog) ? (metadata.activityLog as Record<string, unknown>[]) : []

  const handleStatusTransition = async (nextStatus: FormInstanceStatus.IN_PROGRESS | FormInstanceStatus.COMPLETED) => {
    if (!activeInstance) return

    try {
      await dispatch(
        updateFormInstanceThunk({
          instanceId: activeInstance.id,
          data: {
            status: nextStatus,
            startedAt: nextStatus === FormInstanceStatus.IN_PROGRESS ? new Date().toISOString() : undefined,
            completedAt: nextStatus === FormInstanceStatus.COMPLETED ? new Date().toISOString() : undefined,
          },
        }),
      ).unwrap()

      if (instanceId) {
        dispatch(fetchFormInstanceDetail(instanceId))
      }
      showSuccess(nextStatus === FormInstanceStatus.IN_PROGRESS ? '已開始執行' : '任務已完成')
    } catch (err) {
      const message = err instanceof Error ? err.message : '請稍後再試'
      showError('更新狀態失敗', message)
    }
  }

  const showStartButton = activeInstance?.status === FormInstanceStatus.PENDING
  const showCompleteButton =
    activeInstance &&
    (activeInstance.status === FormInstanceStatus.PENDING || activeInstance.status === FormInstanceStatus.IN_PROGRESS)

  const handleValueChange = useCallback(
    (field: FormTemplateField, nextValue: FieldControlValue) => {
      if (logicState.disabled[field.id]) {
        return
      }
      setFormValues(prev => ({
        ...prev,
        [field.id]: nextValue,
      }))
      setFieldErrors(prev => {
        if (!prev[field.id]) return prev
        const nextErrors = { ...prev }
        delete nextErrors[field.id]
        return nextErrors
      })
    },
    [logicState.disabled],
  )

  const validateFields = useCallback(
    (targetFields: FormTemplateField[]) => {
      const nextErrors: Record<string, string> = {}
      targetFields.forEach(field => {
        if (logicState.visible[field.id] === false) {
          return
        }
        if (logicState.disabled[field.id]) {
          return
        }
        if (paragraphFields.has(field.type)) {
          return
        }
        if (unsupportedFieldTypes.has(field.type)) {
          const enforcedRequired = logicState.required[field.id] ?? field.isRequired
          if (enforcedRequired) {
            nextErrors[field.id] = '此欄位類型尚未支援，請聯絡系統管理員。'
          }
          return
        }
        const rawValue = formValues[field.id]
        const enforcedRequired = logicState.required[field.id] ?? field.isRequired
        if (enforcedRequired && isEmptyValue(rawValue)) {
          nextErrors[field.id] = '此欄位必須填寫'
          return
        }
        const fieldMetadata = (field.metadata ?? {}) as Record<string, unknown>
        if (numberFieldTypes.has(field.type)) {
          const value = typeof rawValue === 'string' ? rawValue.trim() : ''
          if (value.length > 0 && Number.isNaN(Number(value))) {
            nextErrors[field.id] = '請輸入數值'
          }
          if (value.length > 0 && typeof fieldMetadata.min === 'number') {
            const min = fieldMetadata.min as number
            if (Number(value) < min) {
              nextErrors[field.id] = `請輸入不小於 ${min} 的數值`
            }
          }
          if (value.length > 0 && typeof fieldMetadata.max === 'number') {
            const max = fieldMetadata.max as number
            if (Number(value) > max) {
              nextErrors[field.id] = `請輸入不大於 ${max} 的數值`
            }
          }
        }
        if (dateRangeFieldTypes.has(field.type)) {
          const range =
            rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)
              ? (rawValue as DateRangeValue)
              : { startDate: '', endDate: '' }
          const hasStart = Boolean(range.startDate && range.startDate.trim().length > 0)
          const hasEnd = Boolean(range.endDate && range.endDate.trim().length > 0)
          if ((logicState.required[field.id] ?? field.isRequired) && (!hasStart || !hasEnd)) {
            nextErrors[field.id] = '請選擇開始與結束日期'
            return
          }
          if (hasStart && hasEnd) {
            const startTimestamp = Date.parse(range.startDate!)
            const endTimestamp = Date.parse(range.endDate!)
            if (!Number.isNaN(startTimestamp) && !Number.isNaN(endTimestamp) && startTimestamp > endTimestamp) {
              nextErrors[field.id] = '結束日期需晚於開始日期'
            }
          }
        }
        if (multiSelectFieldTypes.has(field.type) && formValues[field.id] && !Array.isArray(formValues[field.id])) {
          nextErrors[field.id] = '資料格式錯誤，請重新選擇'
        }
        if (booleanFieldTypes.has(field.type) && rawValue !== null && rawValue !== undefined && typeof rawValue !== 'boolean') {
          nextErrors[field.id] = '資料格式錯誤，請重新選擇'
        }
      })

      setFieldErrors(nextErrors)
      return nextErrors
    },
    [formValues, logicState.disabled, logicState.required, logicState.visible],
  )

  const buildSubmissionValues = useCallback(
    (targetFields: FormTemplateField[]) => {
      const values: FormSubmissionValue[] = []

      targetFields.forEach(field => {
        if (logicState.visible[field.id] === false) {
          return
        }
        if (logicState.disabled[field.id]) {
          return
        }

        const rawValue = formValues[field.id]

        if (paragraphFields.has(field.type) || unsupportedFieldTypes.has(field.type)) {
          return
        }

        if (fileUploadFieldTypes.has(field.type)) {
          const attachments: UploadedAttachment[] = Array.isArray(rawValue)
            ? (rawValue as unknown[])
                .map(item =>
                  item && typeof item === 'object' && 'id' in (item as Record<string, unknown>) && 'url' in (item as Record<string, unknown>)
                    ? (item as UploadedAttachment)
                    : null,
                )
                .filter((item): item is UploadedAttachment => item !== null)
            : []
          if (attachments.length === 0) {
            return
          }
          values.push({
            fieldId: field.id,
            valueJson: attachments,
          })
          return
        }

        if (dateRangeFieldTypes.has(field.type)) {
          const range =
            rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)
              ? (rawValue as DateRangeValue)
              : undefined
          if (!range) {
            return
          }
          const payload: Record<string, string> = {}
          if (range.startDate && range.startDate.trim().length > 0) {
            const iso = new Date(range.startDate).toISOString()
            payload.startDate = iso
          }
          if (range.endDate && range.endDate.trim().length > 0) {
            const iso = new Date(range.endDate).toISOString()
            payload.endDate = iso
          }
          if (Object.keys(payload).length === 0) {
            return
          }
          values.push({
            fieldId: field.id,
            valueJson: payload,
          })
          return
        }

        if (rawValue === null || rawValue === undefined || rawValue === '') {
          if (!Array.isArray(rawValue)) {
            return
          }
        }

        if (multiSelectFieldTypes.has(field.type)) {
          const list = Array.isArray(rawValue)
            ? rawValue
                .map(item => (typeof item === 'string' ? item.trim() : null))
                .filter((item): item is string => Boolean(item && item.length > 0))
            : []
          if (list.length === 0) return
          values.push({
            fieldId: field.id,
            valueJson: list,
          })
          return
        }

        if (booleanFieldTypes.has(field.type)) {
          if (typeof rawValue !== 'boolean') {
            return
          }
          values.push({
            fieldId: field.id,
            valueBoolean: rawValue,
          })
          return
        }

        if (numberFieldTypes.has(field.type)) {
          if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
            return
          }
          const parsed = Number(rawValue)
          if (Number.isNaN(parsed)) {
            return
          }
          values.push({
            fieldId: field.id,
            valueNumber: parsed.toString(),
          })
          return
        }

        if (dateFieldTypes.has(field.type)) {
          if (typeof rawValue !== 'string' || rawValue.length === 0) {
            return
          }
          if (field.type === FormFieldType.TIME) {
            const iso = new Date(`1970-01-01T${rawValue}`).toISOString()
            values.push({
              fieldId: field.id,
              valueDate: iso,
              valueString: rawValue,
            })
            return
          }
          const iso = new Date(rawValue).toISOString()
          values.push({
            fieldId: field.id,
            valueDate: iso,
          })
          return
        }

        if (textFieldTypes.has(field.type)) {
          const text = typeof rawValue === 'string' ? rawValue.trim() : ''
          if (text.length === 0) {
            return
          }
          values.push({
            fieldId: field.id,
            valueString: text,
          })
          return
        }

        if (selectFieldTypes.has(field.type)) {
          const text = typeof rawValue === 'string' ? rawValue : ''
          if (text.length === 0) {
            return
          }
          values.push({
            fieldId: field.id,
            valueString: text,
          })
        }
      })

      return values
    },
    [formValues, logicState.disabled, logicState.visible],
  )

  const submitForm = useCallback(
    async (status: FormSubmissionStatus) => {
      if (!activeInstance) return
      const applicableFields = fields.filter(field => (logicState.visible[field.id] ?? true))
      const validations = validateFields(applicableFields)
      if (Object.keys(validations).length > 0) {
        showError('欄位填寫有誤', '請檢查紅框提示後再試一次。')
        return
      }

      const payloadValues = buildSubmissionValues(applicableFields)

      if (status === FormSubmissionStatus.SUBMITTED && payloadValues.length === 0) {
        showError('尚未填寫內容', '提交前至少需要填寫一個欄位。')
        return
      }

      try {
        setIsSubmitting(true)
        await dispatch(
          submitFormInstanceThunk({
            instanceId: activeInstance.id,
            data: {
              status,
              values: payloadValues,
            },
          }),
        ).unwrap()

        showSuccess(status === FormSubmissionStatus.SUBMITTED ? '表單已提交完成' : '草稿已儲存')
        dispatch(fetchFormInstanceDetail(activeInstance.id))
      } catch (err) {
        const message = err instanceof Error ? err.message : '請稍後再試'
        showError('提交表單失敗', message)
      } finally {
        setIsSubmitting(false)
      }
    },
    [activeInstance, buildSubmissionValues, dispatch, fields, logicState.visible, showError, showSuccess, validateFields],
  )

  if (loading && !activeInstance) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error && !activeInstance) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>
      </div>
    )
  }

  if (!activeInstance) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-500">找不到表單任務</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Link to={`/project/${projectKey}/forms`} className="text-xs text-primary-600 hover:underline">
            ← 返回表單中心
          </Link>
          <h1 className="text-xl font-semibold text-gray-900">{activeInstance.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${statusBadge(activeInstance.status)}`}>
              <CheckCircleIcon className="h-3.5 w-3.5" />
              {activeInstance.status}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                activeInstance.formType === FormType.PERIODIC
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {activeInstance.formType === FormType.PERIODIC ? '定期檢查' : '一次性表單'}
            </span>
            {typeof activeInstance.sequenceNumber === 'number' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">
                第 {activeInstance.sequenceNumber} 次
              </span>
            )}
            {isLocked && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-600">
                已鎖定
              </span>
            )}
            {activeInstance.dueDate && (
              <span className="inline-flex items-center gap-1">
                <CalendarDaysIcon className="h-4 w-4" /> 截止 {formatDate(activeInstance.dueDate)}
              </span>
            )}
            {autoGenerated && (
              <span className="inline-flex items-center gap-1 text-yellow-600">
                <ExclamationTriangleIcon className="h-4 w-4" /> 系統自動派送
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {showStartButton && (
            <button
              type="button"
              onClick={() => handleStatusTransition(FormInstanceStatus.IN_PROGRESS)}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
            >
              開始執行
            </button>
          )}
          {showCompleteButton && (
            <button
              type="button"
              onClick={() => handleStatusTransition(FormInstanceStatus.COMPLETED)}
              className="inline-flex items-center gap-2 rounded-md border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50"
            >
              標記完成
            </button>
          )}
          {activeInstance.formType === FormType.ONE_OFF && (
            <button
              type="button"
              onClick={async () => {
                if (!activeInstance) return
                try {
                  await dispatch(
                    toggleInstanceLockThunk({ instanceId: activeInstance.id, locked: !isLocked }),
                  ).unwrap()
                  showSuccess(!isLocked ? '已鎖定表單' : '已解除鎖定')
                } catch (err) {
                  const message = err instanceof Error ? err.message : '請稍後再試'
                  showError('更新鎖定狀態失敗', message)
                }
              }}
              disabled={lockUpdating}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium shadow-sm ${
                isLocked
                  ? 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  : 'border border-amber-500 text-amber-600 hover:bg-amber-50'
              } ${lockUpdating ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {lockUpdating ? '處理中…' : isLocked ? '解除鎖定' : '鎖定表單'}
            </button>
          )}
        </div>
      </div>

      {scheduleDate && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
          本任務適用日期：{scheduleDate}
          {scheduleFieldIds > 0 && ` · 須檢查 ${scheduleFieldIds} 項。`}
        </div>
      )}

      {scheduleRule && (
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm text-sm text-gray-600">
          <h2 className="text-base font-semibold text-gray-900">排程資訊</h2>
          <div className="mt-3 space-y-1">
            <div>排程名稱：{scheduleRule.name}</div>
            {scheduleRule.description && <div>說明：{scheduleRule.description}</div>}
            <div>頻率：{scheduleRule.frequency} · 每 {scheduleRule.interval} 週期</div>
            <div>時區：{scheduleRule.timezone}</div>
            <div>起迄：{new Date(scheduleRule.startDate).toLocaleString()} ~ {scheduleRule.endDate ? new Date(scheduleRule.endDate).toLocaleString() : '未設定'}</div>
            {scheduleRule.nextRunAt && <div>下次執行：{new Date(scheduleRule.nextRunAt).toLocaleString()}</div>}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">負責人員</h2>
        {assignments.length > 0 ? (
          <ul className="mt-3 space-y-2 text-sm text-gray-600">
            {assignments.map(assignment => (
              <li key={`${assignment.userId}-${assignment.role}`} className="flex items-center gap-2">
                <span className="font-medium text-gray-800">{assignment.user?.name ?? assignment.userId}</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{assignment.role}</span>
                <span className="text-xs text-gray-400">狀態：{assignment.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-gray-500">尚未指派負責人員。</p>
        )}
      </section>

      {activeInstance.instructions && (
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900">填寫指引</h2>
          <p className="mt-2 whitespace-pre-line text-sm text-gray-600">{activeInstance.instructions}</p>
        </section>
      )}

      {activeInstance.formType === FormType.ONE_OFF && isLocked && lockedAt && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-xs text-yellow-700">
          表單已於 {formatDate(lockedAt)} 鎖定，解除鎖定後才可提交新的修訂版本。
        </div>
      )}

      <section className="space-y-6">
        {groupedFields.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
            此模板尚未設定檢查欄位。
          </div>
        ) : (
          groupedFields.map(group => (
            <div key={group.key} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{group.title}</h2>
                {group.description && <p className="mt-1 text-sm text-gray-500">{group.description}</p>}
              </div>
              <ul className="mt-4 space-y-3">
                {group.fields
                  .filter(field => logicState.visible[field.id] !== false)
                  .map(field => {
                  const fieldMetadata = (field.metadata ?? {}) as Record<string, unknown>
                  const frequency = fieldMetadata.inspectionFrequency as FormInspectionFrequency | undefined
                  const requiredOverride = logicState.required[field.id]
                  const isDisabled = logicState.disabled[field.id] ?? false

                  return (
                    <div key={field.id} className="space-y-2">
                      {frequency && frequency !== 'NONE' && (
                        <div className="text-xs text-primary-600">
                          頻率：{frequencyLabelMap[frequency] ?? frequency}
                        </div>
                      )}
                      <FieldInputRow
                        field={field}
                        value={formValues[field.id]}
                        error={fieldErrors[field.id]}
                        disabled={isDisabled}
                        isRequired={requiredOverride}
                        onChange={(nextValue) => handleValueChange(field, nextValue)}
                      />
                    </div>
                  )
                })}
                {group.fields.every(field => logicState.visible[field.id] === false) && (
                  <div className="rounded border border-dashed border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-500">
                    目前無需填寫的欄位。
                  </div>
                )}
              </ul>
            </div>
          ))
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">提交表單</h2>
        <p className="mt-2 text-sm text-gray-600">
          填寫完成後可直接提交，或先儲存草稿稍後再送出。提交後如需更正，可透過審核流程要求退回。
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => submitForm(FormSubmissionStatus.DRAFT)}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            儲存草稿
          </button>
          <button
            type="button"
            onClick={() => submitForm(FormSubmissionStatus.SUBMITTED)}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            提交表單
          </button>
        </div>
        {isSubmitting && (
          <p className="mt-2 text-xs text-gray-500">正在送出資料，請稍候...</p>
        )}
      </section>

      {activityLog.length > 0 && (
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900">活動紀錄</h2>
          <ul className="mt-3 space-y-3 text-sm text-gray-600">
            {activityLog.map((entry, index) => {
              const actionLabel = typeof entry.action === 'string' ? entry.action : 'ACTION'
              const statusLabel =
                entry.status !== null && entry.status !== undefined ? String(entry.status) : null
              const timestampLabel =
                entry.timestamp !== null && entry.timestamp !== undefined ? String(entry.timestamp) : null
              const note =
                entry.notes !== null && entry.notes !== undefined ? String(entry.notes) : null

              return (
                <li key={index} className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-800">{actionLabel}</span>
                    {statusLabel && <span className="text-xs text-gray-500">狀態：{statusLabel}</span>}
                    {timestampLabel && (
                      <span className="text-xs text-gray-400">
                        {formatDate(timestampLabel)}
                      </span>
                    )}
                  </div>
                  {note && (
                    <p className="mt-1 text-xs text-gray-500">{note}</p>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {submissions.length > 0 && (
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900">提交歷史</h2>
          <div className="mt-3 divide-y divide-gray-100 text-sm text-gray-700">
            {submissions.map(submission => {
              const submittedBy = submission.submittedBy?.name ?? submission.submittedById
              const reviewedBy = submission.reviewedBy?.name ?? submission.reviewedById ?? '—'
              return (
                <div key={submission.id} className="py-3 space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-gray-900">修訂 #{submission.revision}</div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <span>狀態：{submission.status}</span>
                      <span>提交者：{submittedBy}</span>
                      {submission.submittedAt && <span>提交於 {formatDate(submission.submittedAt)}</span>}
                      <span>審核者：{reviewedBy}</span>
                      {submission.reviewedAt && <span>審核於 {formatDate(submission.reviewedAt)}</span>}
                      {submission.isDraft && <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">草稿</span>}
                    </div>
                  </div>
                  {submission.reviewNotes && (
                    <div className="text-xs text-gray-500">審核備註：{submission.reviewNotes}</div>
                  )}
                  {submission.audits && submission.audits.length > 0 && (
                    <div className="rounded border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
                      <div className="font-medium text-gray-600">稽核軌跡</div>
                      <ul className="mt-1 space-y-1">
                        {submission.audits.map(audit => (
                          <li key={audit.id}>
                            [{formatDate(audit.createdAt)}] {audit.action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}

export default ProjectFormInstanceDetail
